import { Location, isPlatformBrowser } from '@angular/common';
import { ApplicationRef, ChangeDetectorRef, DestroyRef, Injectable, PLATFORM_ID, Type, ViewContainerRef, inject } from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationExtras, NavigationStart, Params, Route, Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { Observer, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PrimeModule } from 'src/modules/prime/prime.module';
import { SmTableComponent } from 'src/modules/sm-base/components/sm-table/sm-table.component';
import { InsertionPointDirective } from 'src/modules/sm-base/directives/insertion-point.directive';
import { SmErrorHandler } from 'src/modules/sm-base/misc/sm-error-handler';
import { ButtonDefinition } from 'src/modules/sm-base/models/button-definition.model';
import { Emitter } from 'src/modules/sm-base/models/emitter.model';
import { PrimeMessageSeverity } from 'src/modules/sm-base/models/prime-message-severity.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { AuthenticationService } from 'src/modules/sm-base/services/authentication.service';
import { FeatureModuleService } from 'src/modules/sm-base/services/feature-module.service';
import { FileUploadService } from "src/modules/sm-base/services/file-upload.service";
import { MessageDialogService } from 'src/modules/sm-base/services/message-dialog.service';
import { BackendResponse } from 'src/modules/sm-base/shared/backend-response.model';
import { FrontendFieldError } from "src/modules/sm-base/shared/FrontendFieldError";
import { GuiUtils, GuiUtilsInternal } from 'src/modules/utils/misc/gui-utils';
import { UserInteractionItem } from 'src/modules/utils/misc/user-interaction-item.model';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Map2 } from 'src/modules/utils/shared/map2.model';
import { Objects, ObjectsInternal } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { AppMainComponent } from '../components/app-main/app-main.component';
import { AppTab } from '../models/app-tab.model';
import { ComponentView } from '../models/component-view.model';
import { Config } from '../models/config.model';
import { SaveDataHandler } from '../models/save-data-handler.model';
import { SaveDataResult } from '../models/save-data-result.model';
import { UserNotificationType } from '../models/user-notification-type.model';
import { MainFrontendConfig } from '../netgen/MainFrontendConfig';

@Injectable({
    providedIn: 'root'
})
export class MainAppService {

    //Das bentötigen wir, um aus AuthInterceptor auf den MainAppService zugreifen zu können - andernfalls gibt es bei der Initialisierung einen Dependency Injection Error
    static instance: MainAppService;

    appName: string;
    pageTitle: string;
    menuModel: AppTab[] = [];
    visibleMenuTabs: AppTab[] = [];
    language = "de";

    breadcrumbsVisible = false;
    breadcrumbsInToolBar = true;
    breadcrumbsHome: MenuItem = null;
    breadcrumbsItems: MenuItem[] = [];
    staticHeaderHtml: string;
    showSidebar = true;
    showTopbar = true;
    showSpinner = false;
    storedErrors: any[] = [];
    unreadNotificationCount = 0;
    debugRoutes = false;
    userInteractionHistory: UserInteractionItem[] = [];
    backendVersion = "";
    frontendVersionFromBackend = "";
    loadedDate: Date;
    lastUserInteractionDate: Date;
    mouseX = 0;
    mouseY = 0;
    compactGui = false;
    advancedDeveloper = false;
    
    toolbarBackButton = false;
    useNotifications = false;
    toolbarHomeButton = false;
    toolbarNotificationsButton = false;
    toolbarHelpButton = false;
    toolbarButtons: MenuItem[] = [];
    toolbarShowUser = true;
    multilingual = false;

    contextMenu: MenuItem[];
    contextMenuClass = "";

    changeDetectionCount = 0;
    darkMode: boolean = null;
    darkModeQuery: MediaQueryList;

    mainConfig: MainFrontendConfig = null;

    useFocusTracker = false;
    private focusTrackerOverlay: HTMLDivElement;

    private keyboardListeners: OrdinaryObject<(KeyboardEvent) => void> = {};
    private keyboardListenersByKey: OrdinaryObject<string[]> = {};
    onDarkModeChanged = new Emitter<boolean>();

    onSelectionCallback: () => void;

    private titleService = inject(Title);
    confirmationService = inject(ConfirmationService);

    async showConfirmPopup(message: string): Promise<boolean> {
        const el = AppMainComponent.instance.confirmPopupTargetRef.nativeElement as HTMLElement;
        el.style.left = this.mouseX + "px";
        el.style.top = this.mouseY + "px";
        
        return new Promise((resolve) => {
            this.confirmationService.confirm({
            target: el,
            message,
            acceptLabel: 'Ja',
            rejectLabel: 'Nein',
            accept: () => resolve(true),
            reject: () => resolve(false),
            rejectVisible: true,
            dismissableMask: false
            });
        
            requestAnimationFrame(() => {
                const overlay = Array.from(document.querySelectorAll<HTMLElement>('.p-confirmpopup')).at(-1);
                if (overlay == null) {
                    return;
                }
                const w = overlay.offsetWidth;
                overlay.style.left = Math.max(0, (overlay.offsetLeft - w)) + 'px';
            });
        });
    }

    globalKeyHandler(event: KeyboardEvent): void {
        AppMainComponent.instance?.onKeyPress(event);
    }
       
    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private location: Location,
        public messageService: MessageService,
        public dialog: MessageDialogService,
        public fileUpload: FileUploadService,
        private applicationRef: ApplicationRef,
        public sanitizer: DomSanitizer,
        public auth: AuthenticationService,
        public featureModules: FeatureModuleService,
        public deviceDetector: DeviceDetectorService) {

        const platformId = inject(PLATFORM_ID);
        const destroyRef = inject(DestroyRef);

        if (isPlatformBrowser(platformId)) {
            // WICHTIG: { capture: true } = läuft VOR PrimeNGs Listenern
            window.addEventListener('keydown', this.globalKeyHandler, { capture: true });

            destroyRef.onDestroy(() => {
                window.removeEventListener('keydown', this.globalKeyHandler, { capture: true });
            });
        }

        this.darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this.darkModeQuery.addEventListener('change', () => this.updateDarkMode());
        this.updateDarkMode();

        GuiUtils.checkSingleton(this);
        MainAppService.instance = this;
        let config = Config.get();
        this.updatePageTitle(config.pageTitle);
        this.showSidebar = config.showSidebar;
        this.toolbarBackButton = config.showBackButton;
        this.toolbarHelpButton = config.showHelpButton;
        this.toolbarShowUser = !config.allowAnonymous;
        this.breadcrumbsInToolBar = config.breadcrumbsInToolBar;
        this.staticHeaderHtml = config.staticHeaderHtml;
        this.setCompactGui(config.compactGui);
        this.loadedDate = new Date();
        this.language = localStorage.getItem("language");
        //PRAGMATIC (zukünftig konfigurierbar)
        if (this.language != "de" && this.language != "en" && this.language != "fr" && this.language != "es") {
            this.language = "de";
        }
        this.multilingual = config.multilingual;
        if (config.developer) {
            SmErrorHandler.instance.onError.push(error => this.storedErrors.push(error));
        }

        if (config.backendV2) {
            GuiUtilsInternal.convertCsvToExcelFunction = async (csv, columns) => RestEndpoint.main().body({ text: csv, columns }).post().run("api/app/csvToExcel").getString();
        }

        window.addEventListener('unhandledrejection', (event) => {
            SmErrorHandler.instance.handleError(event.reason);
        });

        this.router.events.subscribe(event => {
            if (this.debugRoutes) {
                console.group(`Router event: ${event.constructor.name}`);
                console.log(event);
                console.groupEnd();
            }
            if (event instanceof NavigationStart) {
                this.dialog.dialogService.dialogComponentRefMap.forEach((_, ref) => ref.close());

                if (config.backendV2 && this.frontendVersionOutdated()) {
                    let lastUpdate = Utils.toNumber(localStorage.getItem("lastAppUpdate"), true, true, 0);
                    let time = new Date().getTime();
                    if (time - lastUpdate >= 60 * 60 * 1000) {
                        localStorage.setItem("lastAppUpdate", Objects.toString(time));
                        void RestEndpoint.main().post().body("Version wird aktualisiert: " + (window as any).frontendVersion + " zu " + this.frontendVersionFromBackend).run("api/app/log").getText().then(() => {
                            window.location.reload();
                        });
                    }
                    //this.showToast("warn", "Update", "Version veraltet. Bitte mit F5 neuladen");
                }

                this.userInteractionHistory.push(new UserInteractionItem(new Date(), "URL", [event.url]));
            }
        });

        (window as any).smDebug = {
            getClassesWithoutStyles: () => {
                const allElements = document.querySelectorAll('*');
                const classWithoutStyles = new Map2<string, number>();

                allElements.forEach(element => {
                    if (typeof element.className === "string") {
                        const classes = element.className.split(/\s+/);
                        classes.forEach(className => {
                            const style = getComputedStyle(element, '.' + className);
                            // Überprüft, ob die resultierenden Stile nur Standardwerte haben
                            if (className != '' && (!style || style.length === 0 || style.cssText === '')) {
                                classWithoutStyles.set(className, classWithoutStyles.getSafe(className, 0) + 1);
                            }
                        });
                    }
                });
                console.log(classWithoutStyles.itemsToString("\n"));
            }
        };

        timer(1_000, 60_000 * 5).subscribe(async _ => {
            await this.checkNotifications();
        });
    }

   
    boundUpdateFocusTrackerOverlay = this.updateFocusTrackerOverlay.bind(this);

    setUseFocusTracker(value: boolean): void {
        this.useFocusTracker = value;
        document.querySelector('#focus-tracker-overlay')?.remove();
         if (this.useFocusTracker) {
            
            this.focusTrackerOverlay = document.createElement('div');
            this.focusTrackerOverlay.id = 'focus-tracker-overlay';
            Object.assign(this.focusTrackerOverlay.style, {
                position: 'absolute',
                pointerEvents: 'none',
                border: '3px solid rgba(255, 0, 0, 0.6)',
                borderRadius: '4px',
                background: 'rgba(255, 0, 0, 0.1)',
                zIndex: 999999,
                transition: 'all 0.1s ease'
                });
            document.body.appendChild(this.focusTrackerOverlay);
            
            document.addEventListener('focusin', this.boundUpdateFocusTrackerOverlay, true);
            document.addEventListener('scroll', this.boundUpdateFocusTrackerOverlay, true);
            window.addEventListener('resize', this.boundUpdateFocusTrackerOverlay);
            this.updateFocusTrackerOverlay();
        }
        else {
            document.removeEventListener('focusin', this.boundUpdateFocusTrackerOverlay);
            document.removeEventListener('scroll', this.boundUpdateFocusTrackerOverlay);
            window.removeEventListener('resize', this.boundUpdateFocusTrackerOverlay);
        }
    }

    private updateFocusTrackerOverlay(): void {
        const el = document.activeElement;
        if (!el || el === document.body) {
            this.focusTrackerOverlay.style.display = 'none';
            return;
        }

        const rect = el.getBoundingClientRect();
        this.focusTrackerOverlay.style.display = 'block';
        this.focusTrackerOverlay.style.top = rect.top + window.scrollY + 'px';
        this.focusTrackerOverlay.style.left = rect.left + window.scrollX + 'px';
        this.focusTrackerOverlay.style.width = rect.width + 'px';
        this.focusTrackerOverlay.style.height = rect.height + 'px';
    }

    toggleDarkMode(alsoToggleAuto?: boolean): void {
        this.darkMode = alsoToggleAuto ? this.darkMode === true ? false : this.darkMode === false ? null : true : !this.isDarkMode();
        this.updateDarkMode();
    }

    isDarkMode(): boolean {
        return Config.get().allowDarkMode && (this.darkMode ?? this.darkModeQuery.matches);
    }
    
    updateDarkMode(): void {
        this.onDarkModeChanged.emit(this.isDarkMode());
        if (this.isDarkMode()) {
            document.documentElement.classList.add("dark-mode");
        }
        else {
            document.documentElement.classList.remove("dark-mode");
        }
    }

    frontendVersionOutdated(): boolean {
        return !Utils.isNoe(this.frontendVersionFromBackend) && this.frontendVersionFromBackend != (window as any).frontendVersion;
    }

    updateMenuVisibility(): void {
        this.visibleMenuTabs = this.menuModel.filter(t => t.visible);
    }

    translation: OrdinaryObject = {};

    async setLanguage(value: string): Promise<void> {
        if (value == null || value != this.language) {
            let lng = value ?? this.language;
            this.translation = Objects.fromJson(await RestEndpoint.main().query({ language: lng }).run("api/app/translation").getText());
            PrimeModule.instance.setLanguage(lng);
            SmTableComponent.setLanguage(lng);
        }
        if (value != null) {
            this.language = value;
            localStorage.setItem("language", value);
        }
    }    

    async checkNotifications(): Promise<void> {
        if (this.auth.isLoggedIn() && this.useNotifications) {
            this.unreadNotificationCount = await RestEndpoint.main().run("api/app/notification/unreadcount").getNumber();
        }
    }

    getUserName(): string {
        return this.auth.getUserName();
    }

    userInteracted(): void {
        this.lastUserInteractionDate = new Date();
    }

    userKeyPress(event: KeyboardEvent): string {
        this.userInteracted();

        let key = event.key;
        if (key == null) {
            return null;
        }

        if (event.altKey) {
            key = "Alt-" + key;
        }
        if (event.ctrlKey) {
            key = "Ctrl-" + key;
        }
        if (event.shiftKey) {
            key = "Shift-" + key;
        }
        key = key.toUpperCase();
        for (let guid of Objects.getSafe(this.keyboardListenersByKey, key, [])) {
            this.keyboardListeners[guid](event);
        }
        return key;
    }

    addKeyboardListener(key: string, callback: (KeyboardEvent) => void): string {
        key = key.toUpperCase();
        let guid = Utils.generateGuid();
        this.keyboardListeners[guid] = callback;
        if (!(key in this.keyboardListenersByKey)) {
            this.keyboardListenersByKey[key] = [];
        }
        this.keyboardListenersByKey[key] = [...this.keyboardListenersByKey[key], guid];
        return guid;
    }

    removeKeyboardListener(guid: string): void {
        delete this.keyboardListeners[guid];
        for (let key of Objects.getPropertyNames(this.keyboardListenersByKey)) {
            this.keyboardListenersByKey[key] = this.keyboardListenersByKey[key].filter(s => s != guid);
        }
    }

    //callback eigentlich vom Typ callback: (params: any) => any, das kollidiert aber mit eslint rule so-unsafe-argument
    subscribeToParamsChanges(route: ActivatedRoute, callback: any): void {
        route.params.subscribe(callback as Partial<Observer<Params>>);
    }

    //callback eigentlich vom Typ callback: (params: any) => any, das kollidiert aber mit eslint rule so-unsafe-argument
    subscribeToQueryParamsChanges(route: ActivatedRoute, callback: any): void {
        route.queryParams.subscribe(callback as Partial<Observer<Params>>);
    }

    handlePortalBackendErrorId(errorId: string): void {
        if (!Utils.isNoe(errorId)) {
            SmErrorHandler.instance.handleBackendError(errorId);
        }
    }

    backendVersionRetrieved(backendVersion: string, frontendVersionFromBackend: string): void {
        if (!Utils.isNoe(backendVersion)) {
            this.backendVersion = Strings.removePrefix(Strings.removeSuffix(backendVersion, "\""), "\"");
        }
        if (!Utils.isNoe(frontendVersionFromBackend)) {
            this.frontendVersionFromBackend = Strings.removePrefix(Strings.removeSuffix(frontendVersionFromBackend, "\""), "\"");
        }
    }

    async navigate(commands: any[], extras?: NavigationExtras): Promise<void> {
        await this.router.navigate(commands, extras);
    }

    async navigateChangeParams(queryParams: OrdinaryObject, replaceUrl = true, skipLocationChange = false): Promise<boolean> {
        return this.router.navigate([], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling: "merge",
            replaceUrl,
            skipLocationChange
        });
    }

    navigateBack(): void {
        this.location.back();
    }

    reloadPage(): void {
        window.location.reload();
    }

    async navigateToUrl(url: string): Promise<void> {
        await this.router.navigateByUrl(url);
    }

    async navigateTo(routerLink: any[], replaceUrl?: boolean, queryParams?: Params): Promise<void> {
        await this.router.navigate(routerLink, { replaceUrl, queryParams });
    }

    async navigateToComponent(component: any, params?: OrdinaryObject, replaceUrl = false): Promise<void> {
        const routes = this.findRoutesForComponent(component);

        if (Utils.isNoe(routes)) {
            throw new Error("Für die Komponente " + component.name + " wurde keine Route gefunden");    
        }

        let neededParams = params != null ? Objects.getPropertyNames(params) : [];
        for (let route of routes) {
            let routeParams = Strings.regexGetGroups(route.path, /:([a-zA-Z0-9]+)/g, 1);
            if (Arrays.equalsFlatUnordered(routeParams, neededParams)) {
                let path = route.path;
                for (let key of neededParams) {
                    path = path.replace(':' + key, encodeURIComponent(params[key]));
                }
                await this.router.navigate([path], {
                    replaceUrl
                });
                return;
            }
        }

        throw new Error("Für die Komponente " + component.name + " wurde keine passende Route für die angegebenen Parameter " + Objects.toJson(params) + " gefunden");        
    }

    private findRoutesForComponent(component: any): Route[] {
        return this.flattenRoutes(this.router.config).filter(r => r.component === component);
    }

    private flattenRoutes(routes: Route[], basePath = ''): Route[] {
        return routes.flatMap(route => {
        const current = {
            ...route,
            path: basePath + (route.path ? '/' + route.path : '')
        };
        return route.children ?? (route as any)._loadedRoutes
            ? [current, ...this.flattenRoutes(route.children ?? (route as any)._loadedRoutes, current.path)]
            : [current];
        });
    }

    updateNavigation(pageTitle: string, breadcrumbsItems: MenuItem[]): void {
        GuiUtils.angularTimer(() => {
            this.updatePageTitle(pageTitle || (breadcrumbsItems.length == 0 ? "Home" : breadcrumbsItems[breadcrumbsItems.length - 1].label));
            this.updateBreadcrumbs(breadcrumbsItems);
        });
    }

    updateBreadcrumbs(breadcrumbsItems: MenuItem[]): void {
        this.breadcrumbsHome = { routerLink: ["/"], icon: 'fas fa-home' };
        this.breadcrumbsItems = Arrays.withoutNull(breadcrumbsItems);
        if (this.breadcrumbsItems.length > 0) {
            delete this.breadcrumbsItems[this.breadcrumbsItems.length - 1].routerLink;
        }
        this.breadcrumbsVisible = Config.get().showBreadcrumbs;
        if (this.breadcrumbsItems.length == 0) {
            this.breadcrumbsItems = null;
        }
    }

    updatePageTitle(pageTitle: string): void {
        this.pageTitle = pageTitle;
        this.titleService.setTitle(pageTitle);
    }

    async updateQueryParams(key: string, value: any): Promise<void> {
        let queryParams: OrdinaryObject = {};
        queryParams[key] = Objects.toString(value);
        await this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
    }

    getEnvironment(): any {
        return environment;
    }

    getInstance(): this {
        return this;
    }

    createComponent<T>(parent: ViewContainerRef | InsertionPointDirective, type: Type<T>): T {
        let viewContainerRef: ViewContainerRef = parent instanceof InsertionPointDirective ? parent.viewContainerRef : parent;
        viewContainerRef.clear();
        return viewContainerRef.createComponent(type).instance;
    }

    showInfoToast(detail: string, summary?: string, duration?: number, overrideService?: MessageService): void {
        this.showToast("info", summary ?? this.t("general.info"), detail, duration, overrideService);
    }

    showSuccessToast(detail: string, summary?: string, duration?: number, overrideService?: MessageService): void {
        this.showToast("success", summary ?? "Erfolgreich", detail, duration, overrideService);
    }

    showErrorToast(detail: string, summary?: string, duration?: number, overrideService?: MessageService): void {
        this.showToast("error", summary ?? this.t("general.error"), detail, duration, overrideService);
    }

    showToast(severity: PrimeMessageSeverity, summary: string, detail: string, duration?: number, overrideService?: MessageService): void {
        (overrideService ?? this.messageService).add({ severity, summary, detail, life: duration });
    }

    changeTheme(theme: string): void {
        this.replaceLink(document.getElementById('theme-css') as HTMLLinkElement, theme);
    }

    changeLayout(layout: string): void {
        this.replaceLink(document.getElementById('layout-css') as HTMLLinkElement, layout);
    }

    async copyToClipboardOrDialog(text: string, dialogTitle: string, copyToastText: string): Promise<void> {
        if (await GuiUtils.copyToClipboard(text)) {
            this.showToast("success", "Kopiert", copyToastText);
        }
        else {
            this.dialog.textArea(dialogTitle, text);
        }
    }

    isAdmin(): boolean {
        return this.auth.userDetails?.getDatabaseUser()?.isAdmin ?? false;
    }

    replaceLink(linkElement: HTMLLinkElement, href: string): void {
        const id = linkElement.getAttribute('id');
        const cloneLinkElement = linkElement.cloneNode(true) as HTMLLinkElement;

        cloneLinkElement.setAttribute('href', href);
        cloneLinkElement.setAttribute('id', id + '-clone');

        linkElement.parentNode.insertBefore(cloneLinkElement, linkElement.nextSibling);

        cloneLinkElement.addEventListener('load', () => {
            linkElement.remove();
            cloneLinkElement.setAttribute('id', id);
        });
    }

    getRouterDebugString(): string {
        return this.getRouterDebugStringInternal('', this.router.config);
    }

    triggerChangeDetection(): void {
        this.applicationRef.tick();
    }

    validateForm(form: FrontendForm, additionalValidation: () => FrontendFieldError[] = null, overrideService?: MessageService): boolean {
        let result = form.validate();
        if (additionalValidation != null) {
            result = [...result, ...additionalValidation()];
        }
        if (!Utils.isNoe(result)) {
            this.showErrorToast("Fehler in der Eingabe: \n\n" + Arrays.itemsToString(result, "\n\n", item => item.message), null, null, overrideService);
            return false;
        }
        return true;
    }

    openUrlInNewTab(url: string): boolean {
        let tab = window.open();
        if (tab != null) {
            tab.location.href = url;
            return true;
        }
        else {
            this.showErrorToast("Ein neuer Tab / ein neues Fenster konnte nicht geöffnet wurde. Möglicherweise wird dies durch Ihre Browsereinstellungen blockiert");
            return false;
        }
    }

    async genericDataHandler<T>(worker: () => Promise<T>, errorTitle?: string, onError?: "toast" | "dialog" | null): Promise<{ ok: boolean; value: T }> {
        try {
            return {ok: true, value: await worker()};
        }
        catch (ex) {
            if (onError != null) {
                let error = BackendResponse.getFromException(ex);
                if (onError == "dialog") {
                    await this.dialog.info(!Utils.isNoe(error.items) ? error.items[0].message : errorTitle, errorTitle);
                }
                else if (onError == "toast") {
                    this.showToast("error", errorTitle, !Utils.isNoe(error.items) ? error.items[0].message : errorTitle);
                }
            }
            return {ok: false, value: null};
        }
    }

    async loadDataHandler<T>(worker: () => Promise<T>, source: ComponentView): Promise<T> {
        try {
            return await worker();
        }
        catch (ex) {
            let error = BackendResponse.getFromException(ex);
            await this.loadDataError(!Utils.isNoe(error?.items) ? error.items[0].message : null, !Utils.isNoe(error?.items) && error.items[0].recoverable, source);
            throw ex;
        }
    }

    async loadDataError(message: string, recoverable: boolean, source: ComponentView): Promise<void> {
        let buttons = [new ButtonDefinition("back", "Zurück", false, "fas fa-backward")];
        if (recoverable) {
            buttons = [new ButtonDefinition("reload", "Erneut versuchen", false, "fas fa-redo-alt"), ...buttons];
        }
        let result = await this.dialog.show(message, "Fehler beim Laden der Daten", buttons);
        if (result == "reload") {
            source.ngOnInit();
        }
        else if (result == "back") {
            this.navigateBack();
        }
    }

    async getClipboardText(errorToast = true): Promise<string> {
        try {
            return await GuiUtils.fromClipboard();
        }
        catch (error) {
            if (error.message.includes("Read permission denied") && errorToast) {
                this.showErrorToast("Der Zugriff auf die Zwischenablage wurde verweigert. Bitte erlauben Sie den Zugriff in Ihren Browsereinstellungen und versuchen Sie es erneut.");
            }
            throw error;
        }
    }

    async saveDataHandler(worker: () => Promise<any>, componentView?: ComponentView, successToast?: boolean, navigateBack?: boolean, successTitle?: string, successText?: string): Promise<[boolean, any]> {
        try {
            let result = await worker();

            if (successToast) {
                this.showToast("success", successTitle ?? this.t("general.updatedTitle"), successText ?? this.t("general.updatedText"));
            }
            if (navigateBack) {
                if (componentView != null) {
                    componentView.skipCloseCheck = true;
                    await componentView.close();
                }
                else {
                    this.navigateBack();
                }
            }

            return [true, result];
        }
        catch (ex) {
            let error = BackendResponse.getFromException(ex);
            await this.dialog.info(!Utils.isNoe(error.items) ? error.items[0].message : "Es trat ein Fehler beim Speichern auf.", "Fehler beim Speichern");
            return [false, null];
        }
    }

    async saveDataHandler2(data: SaveDataHandler): Promise<SaveDataResult> {
        try {
            let result = await data.worker();

            switch (data.successNotificationType) {
                case UserNotificationType.toast:
                    this.showToast("success", data.successTitle ?? this.t("general.updatedTitle"), data.successText ?? this.t("general.updatedText"));
                    break;
                case UserNotificationType.dialog:
                    await this.dialog.info(data.successText ?? this.t("general.updatedText"), data.successTitle ?? this.t("general.updatedTitle"));
                    break;                    
            }

            if (data.navigateBack) {
                if (data.componentView != null) {
                    data.componentView.skipCloseCheck = true;
                }
                this.navigateBack();
            }
            else if (data.navigateTo != null) {
                if (data.componentView != null) {
                    data.componentView.skipCloseCheck = true;
                }
                await this.navigateTo(data.navigateTo);
            }

            return new SaveDataResult(true, result);
        }
        catch (ex) {
            let error = BackendResponse.getFromException(ex);
            switch (data.errorNotificationType) {
                case UserNotificationType.toast:
                    this.showToast("error", data.errorTitle ?? this.t("general.errorTitle"), !Utils.isNoe(error.items) ? error.items[0].message : data.errorText ?? this.t("general.errorText"));
                    break;
                case UserNotificationType.dialog:
                    await this.dialog.info(!Utils.isNoe(error.items) ? error.items[0].message : data.errorText ?? this.t("general.errorText"), data.errorTitle ?? this.t("general.errorTitle"));
                    break;                    
            }
            return new SaveDataResult(false, null);
        }
    }

    async deleteDataHandler<T>(worker: () => Promise<T>, componentView?: ComponentView, successToast?: boolean, navigateBack?: boolean, successTitle?: string, successText?: string): Promise<[boolean, T]> {
        try {
            let result = await worker();

            if (successToast) {
                this.showToast("success", successTitle ?? this.t("general.deletedTitle"), successText ?? this.t("general.deletedText"));
            }
            if (navigateBack) {
                if (componentView != null) {
                    componentView.skipCloseCheck = true;
                }
                this.navigateBack();
            }

            return [true, result];
        }
        catch (ex) {
            let error = BackendResponse.getFromException(ex);
            await this.dialog.info(!Utils.isNoe(error.items) ? error.items[0].message : "Es trat ein Fehler beim Löschen auf.", "Fehler beim Löschen");
            return [false, null];
        }
    }

    updateSuccess(): void {
        this.showToast("success", this.t("general.updatedTitle"), this.t("general.updatedText"));
        this.navigateBack();
    }

    public getTranslationMap(): OrdinaryObject {
        return this.translation;
    }        
   
    public t(key: string, params?: OrdinaryObject, def?: string, warnIfMissing: boolean = null): string {
        let result = Objects.getPropertyNested(this.translation, key, true);
        if (result == ObjectsInternal.PROPERTY_NOT_FOUND) {
            warnIfMissing ??= this.multilingual;
            if (warnIfMissing) {
                console.warn("Übersetzung für Schlüssel " + key + " nicht gefunden");
            }
            return def ?? result;
        }
        return result;
    }

    public tRegex(key: string, value: string): string {
        let result = Objects.getPropertyNested(this.translation, key);
        if (Arrays.is(result)) {
            for (let i = 0; i < result.length; i += 2) {
                value = Strings.replaceAll(value, (result as string[])[i], (result as string[])[i + 1]);
            }
        }
        return value;
    }

    public tRegexHas(key: string): boolean {
        return Arrays.is(Objects.getPropertyNested(this.translation, key));
    }

    public tCrudTitle(isNew: boolean, item: any, entityPath: string): string {
        let gender = this.t(entityPath + ".typeNameGender") ?? "m";
        gender = gender == "f" ? "e" : gender == "n" ? "es" : "er";
        return isNew ? this.t("crud.new") + gender + " " + this.t(entityPath + ".typeName") : this.t(entityPath + ".typeName") + " " + Objects.toString(item);
    }

    public tForm(entityPath: string, form: FrontendForm): FrontendForm {
        for (let field of form.fields) {
            if (Utils.isNoe(field.caption)) {
                field.caption = this.t(entityPath + "." + field.id);
            }
        }
        return form;
    }

    public tTable(entityPath: string, table: TableData): TableData {
        for (let col of table.columns) {
            if (Utils.isNoe(col.caption)) {
                col.caption = this.t(entityPath + "." + col.id);
            }
        }
        return table;
    }

    public getRouteParam(paramName: string): any {
        return this.getRouteParamInternal(this.router.routerState.snapshot.root, paramName);
    }

    private getRouteParamInternal(snapshot: ActivatedRouteSnapshot, paramName: string): any {
        if (Objects.hasProperty(snapshot.params, paramName)) {
            return snapshot.params[paramName];
        }
        else {
            for (let child of snapshot.children) {
                let result = this.getRouteParamInternal(child, paramName);
                if (result != null) {
                    return result;
                }
            }
        }
        return null;
    }

    private getRouterDebugStringInternal(parent: String, config: Route[], level = 0): string {
        let result = "";
        let prefix = " ".repeat(level * 2);
        for (let route of config) {
            result += prefix + parent + '/' + route.path + "\n";
            if (route.children) {
                const currentPath = route.path ? parent + '/' + route.path : parent;
                result += prefix + this.getRouterDebugStringInternal(currentPath, route.children, level + 1) + "\n";
            }
        }
        return result;
    }

    public isMobile(): boolean {
        return this.deviceDetector.isMobile();
    }

    openContextMenu(menu: MenuItem[], styleClass?: string): void {
        this.contextMenu = Arrays.withoutNull(menu);
        this.contextMenuClass = styleClass ?? "";
        GuiUtils.angularTimer(() => {
            AppMainComponent.instance.cm.show(this.getCurrentMousePosEvent());
        });
    }

    getCurrentMousePosEvent(): OrdinaryObject {
        let result: OrdinaryObject = {};
        result.pageX = this.mouseX;
        result.pageY = this.mouseY;
        result.stopPropagation = () => null;
        result.preventDefault = () => null;
        return result;
    }

    setCompactGui(value: boolean): void {
        this.compactGui = value;
        //Die Klasse muss auf diesem Wege direkt auf dem Body gesetzt werden, weil andernfalls Komponenten, 
        //die nicht innerhalb des MainComponent liegen nicht korrekt gestyled werden, z.B. Dialoge von PrimeNG
        if (this.compactGui) {
            document.body.classList.add('compact');
        }
    }

}